import React from 'react';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { AnimatedNumber } from '../../AnimatedNumber';
import type {
  AllowedTagName,
  AppliedToDetectedSubstrings,
  ComponentBasicProps,
  ExtraStylesConfig,
  NonSpringRelatedAnimationConfig,
  NumberFormatConfig,
  ParseRawStringParams,
  SpringRelatedAnimationConfig,
} from '../types';
import {
  getExtraStyles,
  getStyleForDetectedNumber,
  getStyleForSubstring,
  validateDecimalSeparator,
  validateThousandsSeparator,
} from '../utils';
import { numberFormatter } from './numberFormatter';
import { parseRawString } from './parseRawString';

interface ReactNodeListParams
  extends ParseRawStringParams,
    NumberFormatConfig,
    SpringRelatedAnimationConfig,
    NonSpringRelatedAnimationConfig,
    AppliedToDetectedSubstrings {}

const generateReactNodeList = (params: ReactNodeListParams) => {
  const {
    // ParseRawStringParams
    rawString,
    decimalSeparatorForNumberDetection,

    // NumberFormatConfig
    areDecimalPlacesFixed,
    decimalSeparator: ds,
    thousandsSeparator: ts,

    // SpringRelatedAnimationConfig
    animationDuration,
    springFriction,
    springTension,

    // NonSpringRelatedAnimationConfig
    defaultAnimationDelay,
    stringifiedLocalAnimationDelays,
    localIsAnimationDisabled,

    // AppliedToDetectedSubstrings
    classNamesForSubstrings,
    classNamesForSubstringsSkipDetectedNumbers,
    classNamesForDetectedNumbers,
    stylesForSubstrings,
    stylesForSubstringsSkipDetectedNumbers,
    stylesForDetectedNumbers,
  } = params;

  const decimalSeparator = validateDecimalSeparator(ds);
  const thousandsSeparator = validateThousandsSeparator(ts);
  const localAnimationDelays: number[] = JSON.parse(stringifiedLocalAnimationDelays);
  const skeleton = parseRawString({
    rawString,
    decimalSeparatorForNumberDetection,
  });
  const result: ReactNode[] = [];

  let numOfSeenDigitGroups = 0;
  let numOfSeenTextGroups = 0;

  for (let i = 0; i < skeleton.length; i += 1) {
    const detectedSubstring = skeleton[i][0];
    const isGroupText = skeleton[i][1] === -1;
    const key = `${i},${detectedSubstring}`;

    if (isGroupText) {
      result.push(
        <span
          key={key}
          className={clsx(
            classNamesForSubstrings && classNamesForSubstrings(i, detectedSubstring),
            classNamesForSubstringsSkipDetectedNumbers &&
              classNamesForSubstringsSkipDetectedNumbers(numOfSeenTextGroups, detectedSubstring),
          )}
          style={{
            ...getStyleForSubstring(stylesForSubstrings, i, detectedSubstring),
            ...getStyleForSubstring(
              stylesForSubstringsSkipDetectedNumbers,
              numOfSeenTextGroups,
              detectedSubstring,
            ),
          }}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: detectedSubstring }}
        />,
      );

      numOfSeenTextGroups += 1;
    } else {
      const decimalPlaces = skeleton[i][1];
      const detectedNumber = Number(
        detectedSubstring.replace(decimalSeparatorForNumberDetection, '.'),
      );
      const valueFormatter = numberFormatter({
        decimalPlaces,
        areDecimalPlacesFixed,
        decimalSeparator,
        thousandsSeparator,
      });
      const formattedNumber = valueFormatter(detectedNumber);

      result.push(
        <AnimatedNumber
          key={key}
          from={localIsAnimationDisabled[numOfSeenDigitGroups] === '1' ? detectedNumber : 0}
          to={detectedNumber}
          animationDelay={localAnimationDelays[numOfSeenDigitGroups] ?? defaultAnimationDelay}
          valueFormatter={valueFormatter}
          springConfig={{
            duration: animationDuration,
            tension: springTension,
            friction: springFriction,
          }}
          className={clsx(
            classNamesForSubstrings && classNamesForSubstrings(i, detectedSubstring),
            classNamesForDetectedNumbers &&
              classNamesForDetectedNumbers(
                numOfSeenDigitGroups,
                detectedSubstring,
                detectedNumber,
                formattedNumber,
              ),
          )}
          style={{
            ...getStyleForSubstring(stylesForSubstrings, i, detectedSubstring),
            ...getStyleForDetectedNumber(
              stylesForDetectedNumbers,
              numOfSeenDigitGroups,
              detectedSubstring,
              detectedNumber,
              formattedNumber,
            ),
          }}
        />,
      );

      numOfSeenDigitGroups += 1;
    }
  }

  return result;
};

interface FrontOfficeElementParams
  extends ComponentBasicProps,
    ExtraStylesConfig,
    ReactNodeListParams {
  tagName: AllowedTagName;
}

export const generateFrontOfficeElement = ({
  tagName,

  // ComponentBasicProps
  tagProps = {},
  className,
  style = {},

  // ExtraStylesConfig
  isBold,
  isItalic,
  isUnderlined,
  textAlign,
  textColor,
  backgroundColor,

  // ReactNodeListParams
  ...reactNodeListParams
}: FrontOfficeElementParams) =>
  React.createElement(
    tagName,
    {
      ...tagProps,
      className: clsx(tagProps?.className, className),
      style: {
        ...(tagProps?.style ?? {}),
        ...style,
        ...getExtraStyles({
          isBold,
          isItalic,
          isUnderlined,
          textColor,
          backgroundColor,
          textAlign,
        }),
      },
    },
    generateReactNodeList(reactNodeListParams),
  );
